import commonMixin from '../../mixins/_common';
import modalMixin from '../../mixins/_modal';
import flashMessageMixin from '../../mixins/_flash_messages';
import imageSelecter from '../../components/_image_selecter';
import loop from '../../components/_loop-component';
import templateSelecter from '../../components/_template_selecter';
import previewComponent from './_preview-component';

/**
 * テンプレート選択画面(看板作成画面)コンポーネント
 */
export default {
    name: 'SelectTemplate',
    template: '#select_template_template',
    data() {
        return {
            // ショップ全体の設定テンプレートデータ
            template: {},
            // 看板作成用データ(看板パーツのデータ構造)
            create_kanban_data: {},
            // ストア構築中進行率
            create_store_progress_rate: 0,
            // 選択中のテンプレートindex
            selected_template_index: null,
            // ストアクリエイターから取得するデータ
            storecreatorData: null,
            // 連動フラグ
            device_sync: false,
        };
    },
    props: {
        // 編集ID
        edit_id: {
            type: [String, Number],
            require: true
        },
    },
    mixins: [
        commonMixin,
        modalMixin,
        flashMessageMixin
    ],
    components: {
        // 画像選択コンポーネント
        'image-selecter': imageSelecter,
        // ヘッダー編集コンポーネント
        'parts-edit-header': {
            template: '#parts_edit_header_template',
            props: ['parts_data', 'type', 'edit_id', 'device_sync', 'should_lock_vertical_margin'],
            components: {
                'loop': loop,
                'image-selecter': imageSelecter,
            }
        },
        // テンプレート選択コンポーネント
        'template-selecter': templateSelecter,
        // プレビュー
        'preview': previewComponent,
    },
    computed: {
        // テンプレートデータがあるか否か
        hasTemplate: function () {
            return (Object.keys(this.template).length > 0);
        },
        // 看板作成データがあるか否か
        hasCreateKanbanData: function () {
            return (Object.keys(this.create_kanban_data).length > 0);
        },
        // プレビュー用静的HTMLが生成されているか
        hasStaticHtml: function () {
            return (this.hasCreateKanbanData && this.create_kanban_data.static_html);
        },
        // 看板作成タイプがHTML作成タイプか
        isTypeHtml: function () {
            return (this.hasCreateKanbanData && this.create_kanban_data.data.type === 'html');
        },
        // ストア構築中か(構築率が1-100の間中)
        isCreatingStore: function () {
            const rate = this.create_store_progress_rate;
            return (rate > 0 && rate <= 100);
        },
        // ストアはオープニング中か
        isOpeningStore :function () {
            return (this.storecreatorData && this.storecreatorData.opening === 1);
        },
        // 表示ステップ番号
        step: {
            get() {
                // テンプレートデータがセットされたら
                if (this.hasTemplate) {
                    return 3;
                }
                // テンプレートindexが選択されたら
                if (this.selected_template_index !== null) {
                    return 2;
                }
                return 1;
            },
            set(value) {
                if (value <= 2) {
                    this.clearAllSetting();
                }
                if (value <= 1) {
                    this.selected_template_index = null;
                }
            },
        },
    },
    watch: {
        // メイン看板画像が更新されたら確認画面へ
        ['create_kanban_data.data.main_image']: function (newValue, oldValue) {
            if (!newValue || typeof oldValue === 'undefined' || this.isTypeHtml) {
                return;
            }
            this.setStaticHtml();
        },
        // 静的HTMLが変動したら一枚看板の設定の時自動でモーダル開閉する
        ['create_kanban_data.static_html']: function (newValue, oldValue) {
            if (typeof oldValue === 'undefined' || this.isTypeHtml) {
                return;
            }
            if (newValue) {
                this.open();
            } else {
                this.close();
            }
        },
    },
    created: function () {
        // JSで使う用にレンダリングされたJSON文字列をデータとして取得
        const data = document.querySelector('[data-roll="storecreator_data"]');
        if(data) {
            this.storecreatorData = JSON.parse(data.innerHTML);
        }
    },
    emits: ['set-template'],
    methods: {
        // パーツ一覧データを取得
        saveTemplate: async function () {
            // プログレスバーを進める
            if (!await this.startCreateStoreProgress()) {
                return;
            }

            let message = this.exception_error_message;
            try {
                const kanbanData = (this.hasCreateKanbanData) ? this.create_kanban_data : null;
                const response = await axios.post('/build_store/save_template', {
                    _method: 'POST',
                    edit_id: this.edit_id,
                    template_index: this.selected_template_index, // 選択されたストアテンプレート
                    header: kanbanData,
                });
                if (response.data.status == 'OK') {
                    this.template = response.data.data.template;
                    this.device_sync = response.data.data.device_sync;
                    this.close();
                    // エラーメッセージを空にする
                    message = '';
                }
            } catch (e) {
                message = this.getCatchMessage(e);
            }
            if (message) {
                // 構築率を初期化
                this.clearCreateStoreProgress();
                this.appendFlashMessage({
                    level: 'alert-danger',
                    message: message,
                });
            }
        },
        // ヘッダーパーツの初期値を取得(一つ目)
        getPartsHeader: async function () {
            let message = this.exception_error_message;
            let success = false;
            this.$_loading();
            try {
                const response = await axios.get('/get_parts', {
                    params: {
                        type: 'build_store'
                    }
                });
                if (response.data.status == 'OK') {
                    this.create_kanban_data = response.data.data.header.data[0];
                    // 静的HTMLは空にする
                    this.clearStaticHtml();
                    success = true;
                    // エラーメッセージを空にする
                    message = '';
                }
            } catch (e) {
                message = this.getCatchMessage(e);
            }
            this.$_unloading();
            if (message) {
                this.appendFlashMessage({
                    level: 'alert-danger',
                    message: message,
                });
            }
            return success;
        },
        // 看板作成モーダルを開く
        openCreateKanban: async function (type) {
            if (!this.hasCreateKanbanData && !await this.getPartsHeader()) {
                return;
            }
            this.create_kanban_data.data.type = type;
            // 看板作成画面へ
            if (this.isTypeHtml) {
                // this.open();
                return;
            // 画像選択モーダル表示
            } else {
                this.$refs.image_selecter.open();
            }
        },
        // 看板作成を行わずに編集画面へ
        skipCreatingKanban: async function () {
            this.$_loading();
            // headerは空でテンプレートを保存
            await this.saveTemplate();
            this.$_unloading();
            // 編集画面へ
            this.gotoEdit();
        },
        // モーダルを閉じたら静的HTML、構築率をクリア
        closed: function () {
            // 構築率を初期化
            this.clearCreateStoreProgress();
            // 一枚看板画像登録は空にしないと同じURL指定、画像を選んだ時検知しなくなる。
            if (!this.isTypeHtml) {
                this.create_kanban_data.data.main_image = '';
            }
            this.clearStaticHtml();
        },
        // 看板データを元に静的HTMLを取得
        setStaticHtml: async function () {
            this.$_loading();
            let message = this.exception_error_message;
            try {
                const response = await axios.post('/build_store/get_kanban_html', {
                    _method: 'POST',
                    edit_id: this.edit_id,
                    data: this.create_kanban_data,
                });
                if (response.data.status === 'OK') {
                    this.create_kanban_data.static_html = response.data.data;
                    // エラーメッセージを空にする
                    message = '';
                }
            } catch (e) {
                message = this.getCatchMessage(e);
            }
            this.$_unloading();
            if (message) {
                this.appendFlashMessage({
                    level: 'alert-danger',
                    message: message,
                });
            }
        },
        // ストア構築プログレスバーを進める
        startCreateStoreProgress: async function () {
            this.create_store_progress_rate++;
            while (this.create_store_progress_rate < 100) {
                await this.$_sleep(50);
                // 途中でモーダルを閉じて初期化されたれ抜ける
                if (this.create_store_progress_rate === 0) {
                    return false;
                } else {
                    this.create_store_progress_rate++;
                }
            }
            return true;
        },
        // 静的HTMLを削除する
        clearStaticHtml: function () {
            this.create_kanban_data.static_html = '';
        },
        // すべて看板作成設定データを初期化して最初の画面へ切り替える
        clearAllSetting: function () {
            this.template = {};
            this.clearCreateKanbanData();
            this.clearCreateStoreProgress();
        },
        // ストア構築プログレスバーを初期化する(0にする)
        clearCreateStoreProgress: function () {
            this.create_store_progress_rate = 0;
        },
        // 看板データを初期化
        clearCreateKanbanData: function () {
            this.create_kanban_data = {};
        },
        // 編集画面へ
        gotoEdit: function () {
            window.scroll(0, 0);
            this.emitSetTemplate();
        },
        // 初期看板設定画面へ
        gotoKanbanSelectTemplate: function () {
            if (!this.isOpeningStore) {
                this.skipCreatingKanban(); //開店前ストアはスキップ
            }
            this.selected_template_index = this.$refs.templateSelecter.template_index;
        },
        // プレビューを開く
        openPreview: function(template){
            this.$root.$refs.preview.show(template.data);
        },

        // -------------------------
        // emit
        // -------------------------

        // テンプレートデータを親へ通知
        emitSetTemplate: function () {
            this.$emit('set-template', [this.template, this.device_sync]);
        },
    }
};
